/**
 * BloqueiosAlert — por que o cliente ZTNA não consegue entrar, e quanto falta.
 *
 * Lista cada bloqueio ativo (GET /customers/{id}/bloqueios) com o motivo e uma
 * contagem regressiva que anda a cada segundo. Quando um bloqueio chega a zero,
 * chama `onExpirou` uma vez por bloqueio, para a tela buscar a lista de novo.
 */
import { useEffect, useRef, useState } from 'react'
import { Alert, Badge, Group, Stack, Text } from '@mantine/core'
import { IconLock } from '@tabler/icons-react'
import dayjs from 'dayjs'

import type { CustomerBloqueio, CustomerBloqueioTipo } from '@/api/ztnaCustomers'
import { segundosParaLiberar, textoLiberaEm } from './bloqueios'

const TITULO: Record<CustomerBloqueioTipo, string> = {
  senha_errada: 'Senha errada várias vezes',
  cota_links: 'Cota de links de acesso por e-mail esgotada',
  tentativas_ip: 'Tentativas de login demais por IP',
  cota_reset: 'Cota de redefinição de senha esgotada',
}

interface Props {
  bloqueios: CustomerBloqueio[]
  onExpirou: () => void
}

export function BloqueiosAlert({ bloqueios, onExpirou }: Props) {
  const [agora, setAgora] = useState(() => Date.now())
  const avisados = useRef(new Set<string>())

  useEffect(() => {
    if (bloqueios.length === 0) return
    const id = window.setInterval(() => setAgora(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [bloqueios.length])

  useEffect(() => {
    const expirados = bloqueios
      .filter((b) => segundosParaLiberar(b.libera_em, agora) === 0)
      .map((b) => `${b.tipo}|${b.libera_em}|${String(b.detalhe.ip ?? '')}`)
      .filter((k) => !avisados.current.has(k))
    if (expirados.length === 0) return
    expirados.forEach((k) => avisados.current.add(k))
    onExpirou()
  }, [agora, bloqueios, onExpirou])

  if (bloqueios.length === 0) return null

  return (
    <Alert color="orange" variant="light" icon={<IconLock size={16} />} mt="xs">
      <Text size="sm" fw={600}>Cliente bloqueado</Text>
      <Stack gap={6} mt={4}>
        {bloqueios.map((b) => (
          <div key={`${b.tipo}-${String(b.detalhe.ip ?? '')}`}>
            <Group gap={6}>
              <Text size="xs" fw={600}>{TITULO[b.tipo] ?? b.tipo}</Text>
              <Badge size="xs" variant="light" color="orange">
                {textoLiberaEm(b.libera_em, agora)}
              </Badge>
              {!b.desbloqueavel && (
                <Badge size="xs" variant="outline" color="gray">não desbloqueável</Badge>
              )}
            </Group>
            <Text size="xs" c="dimmed">
              {b.motivo} Libera às {dayjs(b.libera_em).format('HH:mm:ss')}.
            </Text>
          </div>
        ))}
      </Stack>
    </Alert>
  )
}
